<template>
  <div class="MapLocationSelection">
    <b-aspect aspect="16:10">
      <MapBox
        class="RealisationDetail__map"
        :access-token="accessToken"
        :map-style.sync="mapStyle"
        :options="mapOptions"
        @load="onMapLoaded"
      >
        <LayerChargingpoints
          :filters="filters"
          :code="geographyCode"
          :focus="coordinates !== null || selectLocationWithRequestContext"
        />
        <LayerRequests
          :realisation-uuid="record.uuid"
          @click="handleOpenRequestPopup"
        />

        <LayerLocationPicker
          v-if="LocationUuid"
          :use-geocoder="false"
          :coordinates="coordinates"
          :locked="true"
        />

        <PopupRequest
          :record="record"
          :uuid="requestUuid"
          @close="handleOpenRequestPopup"
        />

        <PopupChargingLocation
          :locked="locked || connect === false"
          :selected="LocationUuid"
          :alternative="alternative"
          @location="handlePreValidatedLocation"
        />
      </MapBox>
    </b-aspect>
    <div class="AddressWrapper d-flex mt-3 w-100">
      <div class="AddressWrapper__instruction mt-3">
        <h4>Locatie selectie</h4>
        <p>
          Er wordt in deze regio uitsluitend gewerkt met vooraf vastgestelde locaties.
        </p>
        <p v-if="alternative">
          Om een alternatieve locatie voor te stellen, selecteert u een groene locatie op de kaart, klikt u op "Gebruik deze locatie" om de coordinaten en het adres over te nemen.
        </p>
        <p v-if="alternative">
          De voorgestelde locatie uit stap 1 wordt automatisch ontkoppeld.
        </p>
        <p v-if="!alternative">
          Selecteer een groene locatie op de kaart, en klik op "Gebruik deze locatie" om de coordinaten en het adres over te nemen.
        </p>
      </div>
      <div
        v-if="address"
        class="AddressWrapper__data"
      >
        <div class="d-flex flex-column mt-3 w-100">
          <div class="d-flex justify-content-between mr-2">
            <strong>Coordinaten</strong>
            <button
              v-if="!isLinkedLocation && !record.ref"
              type="button"
              class="btn btn-outline-dark btn-sm"
              @click="goToConnectedLocation()"
            >
              Laatst gekoppelde locatie
            </button>
          </div>
          Latitude: {{ coordinates.lat }}<br>
          Longitude: {{ coordinates.lng }}
        </div>
        <div class="d-flex flex-column mt-3 w-100">
          <div class="mr-2">
            <strong>Adres</strong>
          </div>
          {{ formattedAddress }}
        </div>

        <p
          class="text-muted pt-3"
          style="font-size: 0.8rem"
        >
          Referentie: {{ LocationUuid }}
        </p>
      </div>
      <div
        v-else
        class="AddressWrapper__data"
      >
        <p class="d-flex flex-column mt-3 w-100">
          Er is nog geen locatie gekozen.
        </p>
      </div>
    </div>

    <FormField
      type="hidden"
      :value="LocationUuid"
      name="Location.chargingpointUuid"
    />
  </div>
</template>

<script>

import MapBox from '@/components/common/MapBox.vue'
import LayerChargingpoints from '@/components/map/LayerChargingpoints.vue'
import LayerRequests from '@/components/map/LayerRequests.vue'
import LayerLocationPicker from '@/components/map/LayerLocationPicker.vue'
import PopupRequest from '@/components/map/PopupRequest.vue'
import PopupChargingLocation from '@/components/map/PopupChargingLocation.vue'
import { getCoordinatesFromCityName } from '@/services/geocoder'
import { municipalityOptions } from '@/config'


import FormField from '@/components/form/FormField.vue'

import { LegendControl } from '@/services/mapbox'

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

import { mapGetters, mapMutations } from 'vuex'
import Vue from 'vue'
import { mapOptions, visibleRequestsByRealisationStatuses } from '@/config'

export default {
  name: 'MapPreSelectedLocationSelection',
  components: {
    MapBox,
    LayerRequests, LayerChargingpoints, PopupRequest, LayerLocationPicker,
    PopupChargingLocation,
    FormField,
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    values: {
      type: Object,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    connect: {
      type: Boolean,
      default: true,
    },
    alternative: {
      type: Boolean,
      default: false,
    },

    /**
     * Active filters
     */
    filters: {
      type: Array,
      required: false,
      default: null,
    },
    municipalityCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      /**
       * MapBox instance
       */
      map: null,

      /**
       * MapBox SDK reference
       */
      mapbox: null,

      /**
       * MapBox is ready
       */
      loaded: false,

      /**
       * MapBox config
       */
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,

      /**
       * MapBox geocoder field instance
       */
      geocoder: null,

      /**
       * The input values of the Address fields
       */
      addressValues: null,

      /**
       * The UUID of the request pin that was clicked on.
       *  Passed on to the Request Popup
       */
      requestUuid: null,
      center: [],
    }
  },
  computed: {
    ...mapGetters('chargingpoints', [
      'getChargingPointByUUID',
    ]),
    ...mapGetters('relations', [
      'requestUuidsByRealisationUuid',
    ]),
    ...mapGetters('requests', [
      'requestByUuid',
    ]),
    /**
     * The connected requests
     *  list of uuid's
     */
    requestUuids() {
      if (this.record) {
        return this.requestUuidsByRealisationUuid({
          uuid: this.record.getUuid(),
        })
      }
      return []
    },
    hasConnectedRequest() {
      return this.requestUuids.length > 0
    },
    // Coordinates of the first request
    requestCoordinates() {
      if (this.requestUuids.length === 0) return null

      return this.requestByUuid({
        uuid: this.requestUuids[0],
      })?.getCoordinates()
    },

    LocationUuid() {
      if (
        this.record &&
        this.record.cbapi
      ) {
        return this.record.getConnectedLocationUuid({
          category: this.alternative ? 'alternative' : 'selected',
        })
      }

      if (this.addressValues && this.addressValues.Location) {
        return this.addressValues.Location.chargingpointUuid
      }
      return null
    },

    SelectedLocation() {
      if (!this.LocationUuid) return null

      return this.getChargingPointByUUID({
        uuid: this.LocationUuid,
      })
    },

    address() {
      let location = this.SelectedLocation

      if (! location) return null

      if (location.isCBAPI()) {
        return location.address
      }
      return location.data.address
    },

    formattedAddress() {
      if (! this.SelectedLocation) {
        return ''
      }

      if (this.SelectedLocation.isCBAPI()) {
        return this.SelectedLocation.getFormattedAddress()
      }

      return this.address.formatted_address
    },

    /**
     * When creating an RP from a Request, the request coordinates are passed in the query string of the url
     *  Or when no location has been selected yet, but there is a connected request
     */
    selectLocationWithRequestContext() {
      return !! (this.$route.query.lat && this.$route.query.lng) || this.hasConnectedRequest
    },

    /**
     * The viewpoort options differ based on the amount of information available.
     */
    mapOptions() {
      /**
       * We're dealing with an known location we can zoom in on
       */
      if (this.coordinates !== null) {
        return {
          center: [this.coordinates.lng, this.coordinates.lat],
          zoom: 14,
        }
      }

      /**
       * When creating a process from a Request context we are looking
       *  for a location near the Request location
       */
      if (this.selectLocationWithRequestContext) {

        if (this.hasConnectedRequest) {
          let coords = this.requestCoordinates
          return {
            center: [
              coords.lng,
              coords.lat,
            ],
            zoom: 14,
          }
        }

        return {
          center: [this.$route.query.lng, this.$route.query.lat],
          zoom: 14,
        }
      }

      /**
       * When no location is known (yet) and no request context is given
       *  The provided bouding box is that of the Netherlands
       *  The ChargingPointsLayer will zoom in on the selected municipality
       */
      return {
        bounds: [3.218994, 50.590212, 7.404785, 53.742214],
      }
    },

    /**
     * Get the Geography code, use to show Charging Point Locations
     */
    geographyCode() {
      return this.record?.getGeographyCode() || null
    },

    /**
     *
     */
    coordinates() {
      let location = this.SelectedLocation

      if (location) {
        if (location.isCBAPI()) {
          return location.getCoordinates()
        }

        let coords = location.data.coordinates
        return {
          lng: coords[0],
          lat: coords[1],
        }
      } else {
        console.log('COORDINATES ARE NULL')
        return null
      }
    },
    isLinkedLocation () {
      return this.municipalityCode === this.SelectedLocation.code
    },
  },
  watch: {
    /**
     * Be on the look out for address changes triggered from the parent component
     */
    values() {
      this.addressValues = this.values
    },
    municipalityCode(code) {
      if (code !== this.values.Municipality) {
        this.setMapCenterAndLoadChargingPoints()
      }
    },
  },
  created() {
    this.addressValues = this.values
  },
  methods: {
    ...mapMutations('chargingpoints', [
      'replaceLocation',
    ]),
    ...mapMutations('realisation', [
      'updateRecordLocation',
      'replaceRecord',
    ]),
    onMapLoaded({ map, mapbox }) {
      this.loaded = true
      this.map = map
      this.mapbox = mapbox

      // Add the GeoCoder plugin
      // This geocoder is only for navigation purposes
      // License restrictions prevent other usage
      this.geocoder = new MapboxGeocoder({
        accessToken: this.accessToken,
        mapboxgl: this.mapbox,

        countries: mapOptions.countries || 'nl',
        types: 'address',
        language: 'nl',

        collapsed: true,
        placeholder: 'Zoek op adres',
        marker: false,
      })

      // this.geocoder.on('result', this.onGeocoderResult)
      this.map.addControl(
        this.geocoder,
      )

      let legend = new LegendControl({
        items: [{
          label: 'Bestaande laadpaal',
          image: 'chargingpoint-realized',
        }, {
          label: 'In voorbereiding',
          image: 'chargingpoint-in-progress',
        }, {
          label: 'Gevalideerde locatie',
          image: 'chargingpoint-definitive',
        }, {
          label: 'Afgewezen locatie',
          image: 'chargingpoint-rejected',
        }, {
          label: 'Onbeoordeelde aanvraag',
          image: 'request-open',
        }, {
          label: 'Geaccepteerde aanvraag',
          image: 'request-accepted',
        }, {
          label: 'Gekoppeld aan ander proces',
          image: 'request-connected',
        }, {
          label: 'Gekoppeld aan dit proces',
          image: 'request-selected',
        }, {
          label: 'Filter op:',
        }, {
          label: 'Toon aanvragen met afgerond RP',
          toggle: true,
          value: visibleRequestsByRealisationStatuses.completed,
          filter: 'completed',
        }],
      })
      this.map.addControl(
        legend,
      )
    },

    /**
     *
     */
    handleOpenRequestPopup({ uuid }) {
      this.requestUuid = uuid
    },

    /**
     *
     */
    async handlePreValidatedLocation({ uuid }) {
      let success = false

      // Do not allow changes to the location connection
      if (this.connect === 'false') {
        console.log('Connection disconnect is not saved.')
        return
      }

      // Deselected = made available again
      if (uuid === null) {
        success = await this.disconnectCurrentLocation()

        if (success) {
          Vue.set(this.addressValues.Location, 'chargingpointUuid', uuid)
        }

        return
      }

      let location = this.getChargingPointByUUID({ uuid })

      if (! location) return

      success = await this.connectLocation({
        locationUuid: uuid,
      })

      if (success) {
        Vue.set(this.addressValues.Location, 'chargingpointUuid', uuid)

        this.$emit('setCoordinates', {
          code: location.getGeographyCode(),
        })

        this.flyToLocation({ center: location.coordinates })
      }
      // TODO: handle lack of success: show error message
    },

    async disconnectCurrentLocation() {

      let syncedTS = this.record.getSyncedTS()

      const category = this.alternative ? 'alternative' : 'selected'

      let currentLocationUuid = this.record.getConnectedLocationUuid({ category })
      let chargingPointLocation = this.getChargingPointByUUID({ uuid: currentLocationUuid })

      let response = await this.$cbapi.process.setLocationConnection({
        uuid: this.record.getUuid(),
        locationUuid: currentLocationUuid,
        action: 'disconnect',
        category,
        synced: syncedTS,
        locationSynced: chargingPointLocation.getSyncedTS(),
      })

      // TODO: CBAPI - TEST - Get fresh records

      // Get fresh copy of the record
      response = await this.$cbapi.process.getByUuid({
        uuid: this.record.getUuid(),
      })
      this.replaceRecord({
        record: response?.data,
      })

      // update syncedTS
      syncedTS = response.ts
      response = await this.$cbapi.locations.getByUuid({
        uuid: currentLocationUuid,
      })

      syncedTS = Math.max([syncedTS, response.ts])

      this.replaceLocation({
        location: response?.data,
      })

      return syncedTS
    },


    async connectLocation({
      locationUuid,
    }) {

      if (! this.record) return false

      let uuid = this.record.getUuid()

      if (this.connect === 'false') {
        console.log('Connection change not saved.')
      }

      // Skip the API call if the workflow record hasn't ever been saved yet
      // Or if the component is configured to skip making a connection
      if (! uuid || this.connect === false) return true

      const category = this.alternative ? 'alternative' : 'selected'
      let response = null
      let syncedTS = this.record.getSyncedTS()

      // TODO: CBAPI - TEST - Location connection
      if (this.record.hasConnectedLocation({ category })) {
        // The disconnect action results in a changed sync TS, which we use for the subsequent calls
        syncedTS = await this.disconnectCurrentLocation()
      }

      console.log("connectLocation", locationUuid)

      const chargingPointLocation = this.getChargingPointByUUID({ uuid: locationUuid })

      // Make new connection
      response = await this.$cbapi.process.setLocationConnection({
        uuid,
        locationUuid,
        action: 'connect',
        category,
        synced: syncedTS,
        locationSynced: chargingPointLocation.getSyncedTS(),
      })

      // Get fresh copy of the record
      response = await this.$cbapi.process.getByUuid({
        uuid,
      })
      this.replaceRecord({
        record: response?.data,
      })

      response = await this.$cbapi.locations.getByUuid({
        uuid: locationUuid,
      })

      this.replaceLocation({
        location: response?.data,
      })

      return true
    },

    async setMapCenterAndLoadChargingPoints() {
      const name = municipalityOptions.find(o => o.value === this.municipalityCode)?.text
      if (name) {
        this.center = await getCoordinatesFromCityName(name)
        this.flyToLocation({ center: this.center })
      }
    },

    goToConnectedLocation () {
      this.$emit('setCoordinates', {
        code: this.SelectedLocation.code,
      })
      this.flyToLocation({ center: this.coordinates })
    },

    flyToLocation ({ center }) {
      this.map.flyTo({
        center,
        duration: 2000,
        zoom: 14,
        essential: true,
      })
    },
  },
}
</script>

<style lang="scss">
.AddressWrapper {
  &__instruction {
    width: calc(50% - 1rem);
    padding: 1rem;
    margin-right: 1rem;
    background: rgba(0, 0, 0, 0.05); // lighten($info, 40%);
  }
  &__data {
    margin-left: 1rem;
    width: calc(50% - 1rem)
  }
}
</style>
